/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  styled,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import PropTypes from "prop-types";
import AddCompanyModal from "./AddCompanyModal";
import { useDispatch, useSelector } from "react-redux";
import {
  acceptNewUserRequest,
  fetchAcceptUserAPIData,
} from "../../../../Services/redux/global-admin-users/GlobalAdminUsersActions";
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const StyledLabel = styled(Typography)({
  width: "150px",
  height: "19px",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "100%",
  letterSpacing: "0px",
  verticalAlign: "middle",
  color: "#2A2C2D",
});

const StyledLinkText = styled(Typography)({
  fontWeight: 400,
  fontSize: "16px",
  lineHeight: "100%",
  letterSpacing: "0px",
  color: "#0476E2",
  textDecoration: "underline",
  textDecorationStyle: "solid",
  textDecorationThickness: "0%",
  textUnderlineOffset: "0%",
  cursor: "pointer",
});

export default function AcceptNewUserModal({
  userDetails,
  openAcceptNewUserModal,
  handleAcceptUserClose,
  searchTerm,
}) {
  const dispatch = useDispatch();
  const { companies, domains, oem_admin_roles } = useSelector(
    (state) => state.globalAdminUsers
  );

  // Remove selectedCompanyName and setSelectedCompanyName
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  // Add state for selected company details
  const [selectedCompanyDetails, setSelectedCompanyDetails] = useState(null);

  const [oemDomainDropdown, setOemDomainDropdown] = useState(
    domains[0]?.domainAlias || ""
  );

  const [selectedDomainRoles, setSelectedDomainRoles] = useState([]);
  const [selectedRoleNames, setSelectedRoleNames] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [keepDropdownOpen, setKeepDropdownOpen] = useState(false);

  const [checkedMFAEnable, setCheckedMFAEnable] = useState(false);
  const [checkedReadOnlyRole, setCheckedReadOnlyRole] = useState(
    userDetails?.portal === "global-admin"
  );
  const [openAddCompanyModal, setOpenAddCompanyModal] = useState(false);

  useEffect(() => {
    dispatch(
      fetchAcceptUserAPIData(
        userDetails?.portal !== "harman-admin" &&
          userDetails?.portal !== "global-admin"
      )
    );
  }, [userDetails, dispatch]);

  // Reset all form values when modal closes
  useEffect(() => {
    if (openAcceptNewUserModal) {
      // When modal opens, set Read Only Role based on portal type
      setCheckedReadOnlyRole(userDetails?.portal === "global-admin");
    } else {
      setCheckedReadOnlyRole(userDetails?.portal === "global-admin");
      setCheckedMFAEnable(false);
      setSelectedRoleNames([]);
      setSelectedRoleIds([]);
      setRoleDropdownOpen(false);
      setKeepDropdownOpen(false);
      setOemDomainDropdown(domains[0]?.domainAlias || "");
      setSelectedCompanyId("");
      setSelectedCompanyDetails(null);
    }
  }, [openAcceptNewUserModal, domains, companies, userDetails?.portal]);

  useEffect(() => {
    if (oem_admin_roles && oemDomainDropdown) {
      const filteredData = oem_admin_roles.filter(
        (role) => role?.domain === oemDomainDropdown
      );

      setSelectedDomainRoles(filteredData);

      // Reset role selection when domain changes
      setSelectedRoleNames([]);
      setSelectedRoleIds([]);
    } else {
      setSelectedDomainRoles([]);
      setSelectedRoleNames([]);
      setSelectedRoleIds([]);
    }
  }, [oem_admin_roles, oemDomainDropdown]);

  const handleAddCompanyClose = () => {
    setOpenAddCompanyModal(false);
  };

  const handleCheckboxChange = (event) => {
    setCheckedMFAEnable(event.target.checked);
  };

  const handleReadOnlyRoleChange = (event) => {
    const isChecked = event.target.checked;
    setCheckedReadOnlyRole(isChecked);

    // When Read Only Role is toggled, update disabled roles
    if (selectedDomainRoles.length > 0) {
      // Update disabled roles based on Read Only Role checkbox state
      if (isChecked) {
        // When Read Only Role is checked, disable non-readOnly roles
        const nonReadOnlyRoles = new Set(selectedDomainRoles
          .filter(
            (role) =>
              !(role.readOnly === true || role.roleName.includes("READONLY"))
          )
          .map((role) => role.roleName));
        const nonReadOnlyRoleIds = new Set(selectedDomainRoles
          .filter(
            (role) =>
              !(role.readOnly === true || role.roleName.includes("READONLY"))
          )
          .map((role) => role._id));
        setSelectedRoleNames((prev) =>
          prev.filter((roleName) => !nonReadOnlyRoles.has(roleName))
        );
        setSelectedRoleIds((prev) =>
          prev.filter((roleId) => !nonReadOnlyRoleIds.has(roleId))
        );
      } else {
        // When Read Only Role is unchecked, enable all roles

        // Deselect any readOnly roles that were previously selected
        const readOnlyRoles = new Set(selectedDomainRoles
          .filter(
            (role) =>
              role.readOnly === true || role.roleName.includes("READONLY")
          )
          .map((role) => role.roleName));
        const readOnlyRoleIds = new Set(selectedDomainRoles
          .filter(
            (role) =>
              role.readOnly === true || role.roleName.includes("READONLY")
          )
          .map((role) => role._id));
        setSelectedRoleNames((prev) =>
          prev.filter((roleName) => !readOnlyRoles.has(roleName))
        );
        setSelectedRoleIds((prev) =>
          prev.filter((roleId) => !readOnlyRoleIds.has(roleId))
        );
      }

      // Keep the dropdown open if it was already open
      if (roleDropdownOpen) {
        setKeepDropdownOpen(true);
        // Ensure dropdown stays open
        setTimeout(() => {
          setRoleDropdownOpen(true);
          setKeepDropdownOpen(false);
        }, 100);
      }
    }
  };

  // Monitor selected roles and auto-update Read Only Role checkbox
  useEffect(() => {
    if (selectedDomainRoles.length > 0 && selectedRoleNames.length > 0) {
      // Find all readOnly roles in the current domain
      const readOnlyRoles = selectedDomainRoles.filter(
        (role) => role.readOnly === true
      );

      if (readOnlyRoles.length > 0) {
        // Check if any readOnly role is currently selected
        const hasSelectedReadOnlyRole = readOnlyRoles.some((role) =>
          selectedRoleNames.includes(role.roleName)
        );

        // Update Read Only Role checkbox if needed
        if (hasSelectedReadOnlyRole && !checkedReadOnlyRole) {
          setCheckedReadOnlyRole(true);
        } else if (!hasSelectedReadOnlyRole && checkedReadOnlyRole) {
          setCheckedReadOnlyRole(false);
        }
      }
    }
  }, [selectedRoleNames, selectedDomainRoles, checkedReadOnlyRole]);

  const findRoleByPortal = (portal, readOnlyRole) => {
    switch (portal) {
      case "oem-admin":
        return readOnlyRole
          ? "IGNITESTOREOEMADMIN_ROLE_READONLY"
          : "IGNITESTOREOEMADMIN_ROLE";
      case "seller":
        return "IGNITESTORESELLER_ROLE";
      case "domain-approver":
        return "IGNITESTOREDOMAINAPPROVER_ROLE";
      case "global-admin":
        return readOnlyRole
          ? "IGNITESTOREGLOBALADMIN_ROLE_READONLY"
          : "IGNITESTOREGLOBALADMIN_ROLE";
      default:
        return "IGNITESTORESELLER_ROLE";
    }
  };

  const handleAcceptUserRequest = () => {
    // Only proceed if a company is selected (not empty or just whitespace)
    if (selectedCompanyId?.trim()) {
      const userId = userDetails?.id;
      const selectedCompany = companies.find((c) => c.id === selectedCompanyId);
      let acceptUserPayload = {
        status: "ACTIVE",
        domain: oemDomainDropdown || "NONE",
        role: findRoleByPortal(userDetails.portal, checkedReadOnlyRole),
        company: selectedCompany?.name,
        companyWebsite: selectedCompany?.businessEntities?.[0]?.website || "",
        companyPrivacyPolicy:
          selectedCompany?.businessEntities?.[0]?.privacyPolicy || "",
        customMFAEnabled: checkedMFAEnable,
      };

      if (userDetails.portal === "oem-admin") {
        acceptUserPayload = {
          ...acceptUserPayload,
          company: selectedCompany?.name,
          oemAdminRoleIds: selectedRoleIds,
        };
      } else {
        acceptUserPayload = {
          ...acceptUserPayload,
        };
      }

      dispatch(acceptNewUserRequest(acceptUserPayload, userId, searchTerm));

      setOemDomainDropdown(userDetails?.domain || "");
      setSelectedCompanyId(companies[0]?.id || "");
      setSelectedRoleNames([]);
      setSelectedRoleIds([]);
      setCheckedMFAEnable(false);
      setCheckedReadOnlyRole(false);
      handleAcceptUserClose();
    }
  };

  // Check if a valid company is selected
  const isCompanySelected =
    selectedCompanyId && selectedCompanyId.trim().length > 0;

  // Check if role is selected (required for oem-admin portal when Role dropdown is visible)
  const isRoleSelectionRequired = userDetails?.portal === "oem-admin";

  const isRoleSelected = !isRoleSelectionRequired || selectedRoleIds.length > 0;

  // Overall form validation
  // Require a portal to be present; disable Accept & Save if missing
  const hasPortal = Boolean(userDetails?.portal);
  const isFormValid = hasPortal && isCompanySelected && isRoleSelected;

  const handleDropdownChange = (event) => {
    const id = event.target.value;
    setSelectedCompanyId(id);
    const found = companies.find((c) => c.id === id);
    setSelectedCompanyDetails(found || null);
  };

  const handleOEMDropdownChange = (event) => {
    setOemDomainDropdown(event.target.value);
  };

  const handleRoleCheckboxChange = (roleId, roleName, checked) => {
    // Find the role object to check if it has readOnly property
    setSelectedRoleNames((prev) =>
      checked ? [...prev, roleName] : prev.filter((name) => name !== roleName)
    );
    setSelectedRoleIds((prev) =>
      checked ? [...prev, roleId] : prev.filter((id) => id !== roleId)
    );
  };

  return (
    <>
      <BootstrapDialog
        onClose={handleAcceptUserClose}
        aria-labelledby="customized-dialog-title"
        open={openAcceptNewUserModal}
        sx={{
          "& .MuiDialog-root": {
            zIndex: 1300,
          },
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: "40px",
            paddingBottom: "40px",
            paddingLeft: "8px",
            paddingRight: "8px",
            minHeight: "100vh",
            boxSizing: "border-box",
          },
          "& .MuiDialog-paper": {
            position: "relative",
            margin: "0",
            marginTop: "0",
            marginBottom: "0",
          },
        }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: "559px",
              width: "100%",
              height: "auto",
              maxHeight: "calc(100vh - 120px)",
              border: "1px solid #1214151A",
              borderRadius: "8px",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box
          sx={{
            maxWidth: "511px",
            minHeight: "36px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Typography
            component="h2"
            sx={{
              width: "170px",
              height: "22px",
              fontWeight: 500,
              fontSize: "20px",
              lineHeight: "120%",
              letterSpacing: "0%",
              color: "#2A2C2D",
            }}
          >
            Accept New User
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleAcceptUserClose}
            sx={{
              height: "32px",
              width: "32px",
              borderRadius: "4px",
              color: "#12141599",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            gap: "16px",
            flexDirection: "column",
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            <StyledLabel>Email:</StyledLabel>
            <Typography
              sx={{
                width: "207px",
                height: "22px",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "140%",
                letterSpacing: "1%",
                verticalAlign: "middle",
                color: "#5A5B5C",
              }}
            >
              {userDetails?.email}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              width: "100%",
              height: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "auto",
                gap: "24px",
              }}
            >
              <Box
                sx={{
                  maxWidth: "459px",
                  height: "auto",
                  maxHeight: "40px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <StyledLabel sx={{ textWrap: "nowrap" }}>
                  Company Name:
                  {" "}
                  <span style={{ marginLeft: "4px", color: "#B50B00" }}>*</span>
                </StyledLabel>

                <FormControl
                  sx={{
                    width: "297px",
                    height: "40px",
                    border: "1px solid #12141533",
                    borderRadius: "4px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiInputBase-input": {
                      color: "#717273",
                      padding: "4px 8px",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                >
                  <Select
                    value={selectedCompanyId}
                    onChange={handleDropdownChange}
                    renderValue={(selected) => {
                      const company = companies.find((c) => c.id === selected);
                      return company ? company.name : "";
                    }}
                    sx={{
                      height: "40px",
                    }}
                    MenuProps={{
                      slotProps: {
                        paper: {
                          style: {
                            maxHeight: 200,
                          },
                        },
                      },
                    }}
                  >
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <IconButton
                  aria-label="add company"
                  onClick={() => setOpenAddCompanyModal(true)}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: "1px solid #12141533",
                    borderRadius: "4px",
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                }}
              >
                <StyledLabel sx={{ alignSelf: "right" }}>Website:</StyledLabel>
                <StyledLinkText
                  sx={{
                    width: "92px",
                    height: "19px",
                  }}
                >
                  {selectedCompanyDetails?.businessEntities?.[0]?.website ||
                    "N/A"}
                </StyledLinkText>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                }}
              >
                <StyledLabel sx={{ alignSelf: "right" }}>
                  Privacy Policy:
                </StyledLabel>
                <Typography
                  sx={{
                    width: "92px",
                    height: "19px",
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "100%",
                    letterSpacing: "0px",
                    color: "#0476E2",
                    textDecoration: "underline",
                    textDecorationStyle: "solid",
                    textDecorationThickness: "0%",
                    textUnderlineOffset: "0%",
                    cursor: "pointer",
                  }}
                >
                  {selectedCompanyDetails?.businessEntities?.[0]
                    ?.privacyPolicy || "N/A"}
                </Typography>
              </Box>


              <Divider
                sx={{
                  width: "511px",
                  border: "1px solid #12141533",
                }}
              />

              <Box
                sx={{
                  width: "100%",
                  height: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                }}
              >
                {userDetails?.portal !== "harman-admin" &&
                  userDetails?.portal !== "global-admin" && (
                    <Box
                      sx={{
                        maxWidth: "459px",
                        height: "auto",
                        maxHeight: "40px",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <StyledLabel>OEM Domain:</StyledLabel>

                      <FormControl
                        sx={{
                          width: "297px",
                          height: "40px",
                          border: "1px solid #12141533",
                          borderRadius: "4px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "& .MuiInputBase-input": {
                            color: "#717273",
                            padding: "4px 8px",
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "100%",
                            letterSpacing: "0%",
                            display: "flex",
                            alignItems: "center",
                          },
                        }}
                      >
                        <Select
                          value={oemDomainDropdown}
                          onChange={handleOEMDropdownChange}
                          sx={{
                            height: "40px",
                          }}
                          MenuProps={{
                            slotProps: {
                              paper: {
                                style: {
                                  maxHeight: 200,
                                },
                              },
                            },
                            anchorOrigin: {
                              vertical: "top",
                              horizontal: "right",
                            },
                            transformOrigin: {
                              vertical: "top",
                              horizontal: "left",
                            },
                          }}
                        >
                          {domains?.map((domain) => {
                            return (
                              <MenuItem
                                key={domain.id}
                                value={domain.domainAlias}
                              >
                                {domain.domainAlias}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                {userDetails?.portal !== "harman-admin" &&
                  userDetails?.portal !== "global-admin" && (
                    <Box
                      sx={{
                        maxWidth: "459px",
                        height: "auto",
                        maxHeight: "40px",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <StyledLabel>Role:</StyledLabel>
                      {userDetails?.portal === "seller" ? (
                        <Typography
                          sx={{
                            width: "297px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            color: "#5A5B5C",
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "100%",
                            letterSpacing: "0%",
                            paddingLeft: "8px",
                          }}
                        >
                          IGNITESTORESELLER_ROLE
                        </Typography>
                      ) : (
                        <FormControl
                          sx={{
                            width: "297px",
                            height: "40px",
                            border: "1px solid #12141533",
                              // isRoleSelectionRequired &&
                              //   ? "1px solid #B50B00"
                              //   : "1px solid #12141533",
                            borderRadius: "4px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                            "& .MuiInputBase-root": {
                              height: "40px",
                              padding: "0",
                            },
                            "& .MuiInputBase-input": {
                              color: "#717273",
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "100%",
                              letterSpacing: "0%",
                            },
                          }}
                        >
                          <Select
                            multiple
                            open={
                              roleDropdownOpen && selectedDomainRoles.length > 0
                            }
                            onOpen={() => {
                              if (selectedDomainRoles.length > 0) {
                                setRoleDropdownOpen(true);
                              }
                            }}
                            onClose={() => {
                              if (!keepDropdownOpen) {
                                setRoleDropdownOpen(false);
                              }
                            }}
                            value={selectedRoleNames}
                            onChange={() => {}} // Prevent default onChange as we handle it with checkboxes
                            displayEmpty
                            renderValue={(selected) => {
                              if (selected.length === 0) {
                                return "Select roles";
                              }
                              const displayText = selected.join(", ");
                              // Truncate if too long (approximate character limit)
                              if (displayText.length > 25) {
                                return displayText.substring(0, 25) + "...";
                              }
                              return displayText;
                            }}
                            sx={{
                              height: "40px",
                              width: "100%",
                              "& .MuiSelect-select": {
                                padding: "10px 32px 10px 8px !important", // More specific padding
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                boxSizing: "border-box",
                              },
                              "& .MuiSelect-icon": {
                                right: "8px",
                                top: "50%",
                                transform: "translateY(-50%)",
                              },
                              "& .MuiInputBase-root": {
                                padding: "0 !important",
                              },
                            }}
                            MenuProps={{
                              slotProps: {
                                paper: {
                                  style: {
                                    maxHeight: 200,
                                  },
                                },
                              },
                              anchorOrigin: {
                                vertical: "top",
                                horizontal: "right",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                              autoFocus: false,
                              disableAutoFocus: true,
                            }}
                          >
                            {selectedDomainRoles?.map((role) => {
                              const isSelected = selectedRoleNames.includes(
                                role.roleName
                              );
                              // Disable readOnly:true if Read Only Role is unchecked, else disable readOnly:false
                              const isDisabled = checkedReadOnlyRole
                                ? role.readOnly === false
                                : role.readOnly === true;
                              return (
                                <MenuItem
                                  key={role._id}
                                  value={role.roleName}
                                  disabled={isDisabled}
                                  onClick={(e) => {
                                    if (!isDisabled) {
                                      handleRoleCheckboxChange(
                                        role._id,
                                        role.roleName,
                                        !isSelected
                                      );
                                    }
                                  }}
                                  sx={{
                                    opacity: isDisabled ? 0.5 : 1,
                                    cursor: isDisabled
                                      ? "not-allowed"
                                      : "pointer",
                                  }}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    disabled={isDisabled}
                                  />
                                  <Typography
                                    sx={{
                                      ml: 1,
                                      color: isDisabled ? "#999" : "inherit",
                                    }}
                                  >
                                    {role?.roleName}
                                  </Typography>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  )}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <StyledLabel sx={{ textWrap: "nowrap" }}>
                    MFA Authentication:
                  </StyledLabel>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Checkbox
                      checked={checkedMFAEnable}
                      onChange={handleCheckboxChange}
                      color="primary"
                    />
                    <Typography
                      sx={{
                        width: "116px",
                        height: "22px",
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: "140%",
                        letterSpacing: "1%",
                      }}
                    >
                      Enable
                    </Typography>
                  </div>
                </Box>
              </Box>

              {userDetails?.portal && userDetails.portal !== "seller" && (
                <Box
                  sx={{
                    maxWidth: "727px",
                    minHeight: "63px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <StyledLabel sx={{ textWrap: "nowrap" }}>
                      Read Only Role:
                    </StyledLabel>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <Checkbox
                        checked={checkedReadOnlyRole}
                        onChange={handleReadOnlyRoleChange}
                        disabled={userDetails?.portal === "global-admin"}
                        color="primary"
                      />
                      <Typography
                        sx={{
                          width: "116px",
                          height: "22px",
                          fontWeight: 400,
                          fontSize: "16px",
                          lineHeight: "140%",
                          letterSpacing: "1%",
                        }}
                      >
                        Enable
                      </Typography>
                    </div>
                  </Box>
                </Box>
              )}

              <DialogActions>
                <Button
                  sx={{
                    textTransform: "none",
                  }}
                  variant="outlined"
                  onClick={handleAcceptUserClose}
                >
                  Cancel
                </Button>
                <Button
                  sx={{
                    textTransform: "none",
                    opacity: isFormValid ? 1 : 0.5,
                  }}
                  variant="contained"
                  onClick={handleAcceptUserRequest}
                  disabled={!isFormValid}
                >
                  Accept & Save
                </Button>
              </DialogActions>
            </Box>
          </Box>
        </Box>
      </BootstrapDialog>

      <AddCompanyModal
        companies = {companies}
        openAddCompanyModal={openAddCompanyModal}
        handleAddCompanyClose={handleAddCompanyClose}
      />
    </>
  );
}

AcceptNewUserModal.propTypes = {
  userDetails: PropTypes.object,
  openAcceptNewUserModal: PropTypes.bool,
  handleAcceptUserClose: PropTypes.func,
  searchTerm: PropTypes.string,
};
